import {
    Container,
    Rectangle,
    Sprite,
    Texture,
    InteractionEvent,
    BitmapFont
} from 'pixi.js';
import { Game, } from '../../game';
import { Scene } from '../Scene';
import { StoryScene } from '../03-story/storyScene';
import { DwellPanel } from './DwellPanel';
import { Button, FocusManager, InteractiveWrapper, Focusable } from '../../ui';
import * as FontFaceObserver from 'fontfaceobserver';
import { Disposable, DisposableGroup } from '../../utils';
import KeyboardJS from 'keyboardjs';

let FONTS_LOADED = false;

export class TitleScene extends Scene
{
    private background: Sprite;
    private buttons: Button[];

    private gestureCatcher: Container;
    private disposables: DisposableGroup;

    constructor(game:Game)
    {
        super(game);
    }

    async preload()
    {
        await super.preload();

        const {loader} = this;
        loader.add('titleBG', './assets/title/cyberchase_echolocation_splashscreen{size}.jpg');
        loader.add('TitleAtlas', './assets/title/TitleAtlas{size}.json');
        Button.preload();

        const loadComplete = loader.load();

        if (!FONTS_LOADED)
        {
            // preload all the fonts we need as well
            const buttonFont = new FontFaceObserver('Asap');// buttons, captions
            const uiFont = new FontFaceObserver('AsapMedium');// UI labels outside of game
            const gameFont = new FontFaceObserver('Nunito');// gameplay labels

            await Promise.all([loadComplete, buttonFont.load(), uiFont.load(), gameFont.load()]);
        }
        else
        {
            await loadComplete;
        }

        this.setup();
    }

    private setup()
    {
        if (!FONTS_LOADED)
        {
            // set up fonts
            BitmapFont.from('Asap', {fill: 0xffffff, fontFamily: 'Asap', fontSize: 60}, {chars: [['A', 'Z'], ['a', 'z'], ['0', '9'], ' ', ':', '!', '\'', '.', '?'] as any});
            BitmapFont.from('AsapMedium', {fill: 0xffffff, fontFamily: 'AsapMedium', fontSize: 60}, {chars: [['A', 'Z'], ['a', 'z'], ['0', '9'], ' ', ':', '!', '\'', '.', '?'] as any});
            (window as any).nunito = BitmapFont.from('Nunito', {fill: 0xffffff, fontFamily: 'Nunito', fontSize: 60}, {chars: [['A', 'Z'], ['a', 'z'], ['0', '9'], ' ', ':', '!', '\'', '.', '?'] as any});

            FONTS_LOADED = true;
        }

        this.game.settings.gridGameScene.value = false;

        // Hidden text for screen reader
        const fragment = this.create('<div><p>Echo Explorers is a self-voicing game. For best play experience, please silence your screen reader speech output and activate the audio description by pressing the space bar.</p></div>');
        document.body.insertBefore(fragment, document.body.childNodes[0]);

        // Background image
        const texture = this.loader.getAsset<Texture>('titleBG');
        this.background = new Sprite(texture);
        this.addChild(this.background);

        // Keyboard input
        KeyboardJS.setContext('title');
        const keyListeners = {
            space: (e:KeyboardJS.KeyEvent) => {
                e.preventRepeat();

                if (this.game.settings.playAudioDescriptions.value) {
                    this.playAudioDescription();
                } else {
                    this.turnOnAudioDescription();
                }
            }
        }

        this.disposables = new DisposableGroup();
        // keys bound in a context are attached to just that context
        for (const key in keyListeners)
        {
            this.disposables.add(Disposable.fromKey(key, keyListeners[key]));
        }

        // Tap Input
        this.gestureCatcher = new Container();
        this.addChild(this.gestureCatcher);
        this.gestureCatcher.interactive = true;
        this.gestureCatcher.hitArea = new Rectangle(0, 0, 2160, 1080);
        this.gestureCatcher.on( 'pointertap', (evt: InteractionEvent) =>{
            // Bottom half of screen.
            if (evt.data.global.y > 540) {
                if (this.game.settings.playAudioDescriptions.value) {
                    this.playAudioDescription();
                } else {
                    this.turnOnAudioDescription();
                }
            }
        });

        this.buttons = [];

        //* a clickable label to cause a scene change
        const playButton = new Button(
            this.loader.getTexture('btn_play', 'TitleAtlas'),
            {
                content: 'PLAY',
                style: {
                    fontName: 'Asap',
                    fontSize: 58,
                    tint: 0x000000,
                },
                offset: {x: 35, y: -10}
            }
        );

        this.disposables.add(new InteractiveWrapper(playButton, {
            onHover: () => {
                this.game.vo.playHighlight( "CYB_Panel_Play" );
            },
            onTap: () => {
                settingsButton.enabled = false;
                playButton.enabled = false;
                this.game.vo.stop();
                this.game.logEvent("startGame", "");
                // when the label is clicked, preload the game scene and then tell the app to switch scenes
                const nextScene = new StoryScene( this.game );
                this.game.app.state.scene.value = nextScene;
            }
        }));
        this.buttons.push( playButton );
        playButton.position.set(1350, 977);
        this.addChild(playButton);

        // Settings button
        const settingsButton = new Button(
            this.loader.getTexture("btn_setupgame", 'TitleAtlas'),
            {
                content: 'Set Up My Game',
                style: {
                    fontName: 'Asap',
                    fontSize: 42,
                    tint: 0x000000,
                },
                offset: {x: 45, y: -10}
            }
        );
        this.disposables.add(new InteractiveWrapper(settingsButton, {
            onHover: () => {
                this.game.vo.playHighlight( "CYB_Panel_SetUp" );
            },
            onTap: () => {
                settingsButton.enabled = false;
                playButton.enabled = false;

                this.game.vo.stop();

                this.game.showAccessibilityPopover( ()=>{
                    // Go to game.
                    this.game.logEvent("startGame", "");
                    const nextScene = new StoryScene( this.game );
                    this.game.app.state.scene.value = nextScene;
                });
            }
        }));
        settingsButton.position.set(840, 977);
        this.addChild(settingsButton);
        this.buttons.push( settingsButton );

        const dwellPanel = new DwellPanel(this.game);
        dwellPanel.position.set(1525, 90);
        // dwellPanel.createHitArea(40);
        this.addChild(dwellPanel);

        FocusManager.instance.activateContext( (this.buttons as Focusable[]).concat(dwellPanel.getFocusItems()) );

    }

    async start()
    {
        // Loader anim is complete, scene can start
        await this.game.vo.playVO('CYB_Launch_EchoExplorers', true);
        // Intro Audio Description line
        if (this.game.settings.playAudioDescriptions.value) {
            // Play audio description.
            this.game.vo.playVO( "Cyberchase_Splash_AD", true);
        } else {
            // Alert about audio descriptions.
            this.game.vo.playVO( "CYB_Launch_AD", true);
        }
    }

    update(_deltaTime:number)
    {
        // nothing to do
    }

    turnOnAudioDescription()
    {
        if (this.game.settings.playAudioDescriptions.value == false )
        {
            this.game.logEvent("change_setting", "audio_description_on");
            this.game.settings.playAudioDescriptions.value = true;
            this.playAudioDescription( true );
        }
    }

    playAudioDescription( overrideCurrentAudio:boolean = false )
    {
        // Play audio description.
        if ( this.shouldPlayAD && (!this.game.vo.playing || overrideCurrentAudio) )
        {
            this.game.vo.playVO( ["Cyberchase_Splash_AD", "CYB_Launch_ADReplay"], true);
        }
    }

    // insert off-screen text for screen reader
    create(htmlStr) {
    let frag = document.createDocumentFragment(),
        temp = document.createElement('div');
    temp.innerHTML = htmlStr;
    while (temp.firstChild) {
        frag.appendChild(temp.firstChild);
    }
    return frag;
}

    cleanup()
    {
        super.cleanup();

        this.game.vo.stop();

        FocusManager.instance.popContext();
        super.destroy({children: true});

        KeyboardJS.setContext('global');
        this.disposables.destroy();
    }
}